import { subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { stateToTzMap } from '@ourbranch/state-to-tz-map';

/**
 * Front-end Rate Control Date hold-window helpers — GitHub issue #32130.
 *
 * rateControlDate (RCD) is the rater's as-of date. Brands may "hold" it for a window
 * instead of forcing it to be today at bind:
 *   - maxStaleDaysToBind (X): default days the RCD may lag today and still bind, and below
 *     which staff-fe should not nag to update.
 *   - underwriterMaxHoldDays (Y): the larger window an underwriter may opt into at bind via
 *     the "Hold Rate Control Date" checkbox.
 * These mirror the backend (charge-policy-step / OfferRaterManager) so the FE shows the
 * same fields and gates bind on the same thresholds. Branch is X=0 / Y=0 (legacy behavior).
 */

export const DEFAULT_RATE_CONTROL_DATE_CONFIG = { maxStaleDaysToBind: 0, underwriterMaxHoldDays: 0 };

/** The brand's rateControlDate config, defaulting to {0,0} (Branch / unconfigured). */
export function getRateControlDateConfig(brandConfig) {
  return brandConfig?.rateControlDate ?? DEFAULT_RATE_CONTROL_DATE_CONFIG;
}

/** Days the RCD may lag today for THIS bind: the underwriter Y window when held, else X. */
export function getBindThresholdDays(config, hold) {
  return hold ? config.underwriterMaxHoldDays : config.maxStaleDaysToBind;
}

/** Show the read-only Rate Control Date field only to underwriters on brands that hold (X > 0). */
export function shouldShowRateControlDateField(canApproveUWReview, config) {
  return Boolean(canApproveUWReview && config.maxStaleDaysToBind > 0);
}

/** Show the "Hold Rate Control Date" checkbox only to underwriters on brands with a Y window. */
export function shouldShowHoldCheckbox(canApproveUWReview, config) {
  return Boolean(canApproveUWReview && config.underwriterMaxHoldDays > 0);
}

/**
 * True when the RCD is more than `maxDaysOld` days behind today in the offer state's
 * timezone. Lenient by design: returns false (not stale) when the RCD or the state's
 * timezone is unknown, matching the existing isStale guard — the backend bind gate is the
 * authoritative check. Compares 'YYYY-MM-DD' strings (which sort chronologically) to avoid
 * timezone-math hazards (see offer-store #30635).
 */
export function isRateControlDateOlderThan({ rateControlDate, state, maxDaysOld, now = new Date() }) {
  const tz = state ? stateToTzMap[state] : null;
  if (!rateControlDate || !tz) {
    return false;
  }
  const cutoffStr = formatInTimeZone(subDays(now, maxDaysOld), tz, 'yyyy-MM-dd');
  return rateControlDate < cutoffStr;
}
